import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import PerformanceCard from './PerformanceCard';
import CircularGauge from './CircularGauge';
import AlertBadge from './AlertBadge';

const OperationsSection = ({ data, onNavigate, formatCurrency }) => {
  const {
    on_time_delivery_rate,
    average_delivery_time,
    transport_efficiency,
    route_optimization_savings,
    total_incidents,
    incidents_by_type,
    incidents_by_severity,
    average_resolution_time,
    incidents_trend,
    transporter_performance,
    best_performing_transporter,
    underperforming_transporters,
    operational_costs_breakdown,
    cost_per_operation,
    cost_efficiency_trend,
    equipment_utilization_rate,
    maintenance_costs,
    equipment_downtime,
    operations_by_region,
    revenue_by_route,
    client_satisfaction_by_route,
    seasonal_performance,
    critical_routes,
    improvement_opportunities,
    capacity_utilization,
    pending_maintenance
  } = data;

  // Données pour le graphique des incidents par type
  const incidentsTypeData = Object.entries(incidents_by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
    color: getIncidentTypeColor(type)
  }));

  function getIncidentTypeColor(type) {
    const colors = {
      'retard': '#EF4444',
      'qualite': '#F59E0B',
      'comportement': '#8B5CF6',
      'prix': '#10B981',
      'perte': '#DC2626',
      'autre': '#6B7280'
    };
    return colors[type] || '#6B7280';
  }

  // Données pour le graphique des coûts opérationnels
  const costsData = Object.entries(operational_costs_breakdown).map(([category, amount]) => ({
    name: category.charAt(0).toUpperCase() + category.slice(1),
    value: amount
  }));

  // Données pour les régions
  const regionsData = Object.entries(operations_by_region).map(([region, count]) => ({
    name: region,
    count: count
  }));

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'élevée': return 'bg-red-100 text-red-800';
      case 'moyenne': return 'bg-yellow-100 text-yellow-800';
      case 'faible': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 90) return { bg: 'bg-green-100', text: 'text-green-800', indicator: 'bg-green-500' };
    if (rate >= 80) return { bg: 'bg-yellow-100', text: 'text-yellow-800', indicator: 'bg-yellow-500' };
    if (rate >= 70) return { bg: 'bg-orange-100', text: 'text-orange-800', indicator: 'bg-orange-500' };
    return { bg: 'bg-red-100', text: 'text-red-800', indicator: 'bg-red-500' };
  };

  return (
    <div className="space-y-8">
      {/* En-tête Operations */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center">
          <span className="mr-3">🚛</span>
          Opérations - Logistique & Gestion des Incidents
        </h2>
        
        {/* Indicateurs principaux */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <CircularGauge
              title="Livraisons à Temps"
              value={on_time_delivery_rate}
              max={100}
              size={100}
              color="#22C55E"
              unit="%"
            />
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {average_delivery_time.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">jours</div>
            <div className="text-xs text-gray-500 mt-1">Temps moyen livraison</div>
          </div>
          
          <div className="text-center">
            <CircularGauge
              title="Efficacité Transport"
              value={transport_efficiency}
              max={100}
              size={100}
              color="#3B82F6"
              unit="%"
            />
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(route_optimization_savings)}
            </div>
            <div className="text-xs text-gray-500 mt-1">Économies optimisation</div>
          </div>
        </div>
      </div>

      {/* Performance logistique détaillée */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PerformanceCard
          title="Utilisation Capacité"
          value={capacity_utilization.toFixed(1)}
          unit="%"
          change={capacity_utilization >= 85 ? "Optimal" : capacity_utilization >= 70 ? "Bon" : "À améliorer"}
          changeLabel="niveau"
          icon="📦"
          color={capacity_utilization >= 85 ? "green" : capacity_utilization >= 70 ? "yellow" : "red"}
          onClick={() => onNavigate('capacity-analysis')}
        />
        
        <PerformanceCard
          title="Équipements Utilisés"
          value={equipment_utilization_rate.toFixed(1)}
          unit="%"
          change={`${equipment_downtime.toFixed(1)}h d'arrêt`}
          changeLabel="temps d'arrêt"
          icon="⚙️"
          color="blue"
          onClick={() => onNavigate('equipment-status')}
        />
        
        <AlertBadge
          type={pending_maintenance > 5 ? "error" : pending_maintenance > 2 ? "warning" : "neutral"}
          title="Maintenances en Attente"
          description="Interventions programmées"
          count={pending_maintenance}
          icon="🔧"
          pulse={pending_maintenance > 5}
          onClick={() => onNavigate('maintenance-schedule')}
        />
      </div>

      {/* Gestion des incidents */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <span className="mr-2">🚨</span>
          Gestion des Incidents
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">{total_incidents}</div>
            <div className="text-sm text-gray-600">Total incidents</div>
            <div className={`text-xs mt-2 px-2 py-1 rounded-full ${
              incidents_trend < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {incidents_trend >= 0 ? '+' : ''}{incidents_trend.toFixed(1)}% ce mois
            </div>
          </div>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {average_resolution_time.toFixed(1)}h
            </div>
            <div className="text-sm text-gray-600">Temps résolution moyen</div>
            <div className={`text-xs mt-2 px-2 py-1 rounded-full ${
              average_resolution_time <= 24 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {average_resolution_time <= 24 ? 'Excellent' : 'À améliorer'}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700 mb-2">Répartition par gravité</div>
            {Object.entries(incidents_by_severity).map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(severity)}`}>
                  {severity.charAt(0).toUpperCase() + severity.slice(1)}
                </span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Graphique incidents par type */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Incidents par Type</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incidentsTypeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    dataKey="value"
                    label={({name, value}) => value > 0 ? `${name}: ${value}` : ''}
                  >
                    {incidentsTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Routes critiques */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Routes Critiques</h4>
            <div className="space-y-3">
              {critical_routes.map((route, index) => (
                <div key={index} className={`p-3 rounded-lg border-l-4 ${
                  route.risk_level === 'élevé' ? 'border-red-500 bg-red-50' :
                  route.risk_level === 'moyen' ? 'border-yellow-500 bg-yellow-50' :
                  'border-green-500 bg-green-50'
                }`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-sm">{route.route}</div>
                      <div className="text-xs text-gray-600">
                        Risque: {route.risk_level} • {route.incidents} incident(s)
                      </div>
                    </div>
                    <button 
                      onClick={() => onNavigate('route-details', { route: route.route })}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Analyser →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance transporteurs */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <span className="mr-2">🏆</span>
          Performance des Transporteurs
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Meilleur transporteur */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-green-800">🥇 Top Performeur</h4>
              <span className="text-2xl">🏆</span>
            </div>
            <div className="space-y-2">
              <div className="font-medium text-gray-900">{best_performing_transporter.name}</div>
              <div className="text-sm text-gray-600">
                {best_performing_transporter.operations_count} opérations
              </div>
              <div className="text-sm font-medium text-green-600">
                {best_performing_transporter.on_time_rate?.toFixed(1)}% à temps
              </div>
            </div>
          </div>
          
          {/* Transporteurs sous-performants */}
          <div className="col-span-2">
            <h4 className="font-medium text-gray-900 mb-4">Nécessitent Attention</h4>
            <div className="space-y-2">
              {underperforming_transporters.slice(0, 3).map((transporter, index) => {
                const style = getPerformanceColor(transporter.on_time_rate);
                return (
                  <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${style.bg}`}>
                    <div>
                      <div className="font-medium text-sm">{transporter.name}</div>
                      <div className="text-xs text-gray-600">
                        {transporter.operations_count} opérations
                      </div>
                    </div>
                    <div className={`text-sm font-medium ${style.text}`}>
                      {transporter.on_time_rate.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Graphique performance transporteurs */}
        {transporter_performance.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Performance Globale</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={transporter_performance.slice(0, 8)}>
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'on_time_rate') return [`${value.toFixed(1)}%`, 'Taux à temps'];
                      if (name === 'total_revenue') return [formatCurrency(value), 'CA Total'];
                      return [value, name];
                    }}
                  />
                  <Bar dataKey="on_time_rate" fill="#22C55E" name="on_time_rate" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Coûts et efficacité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">💰</span>
            Coûts Opérationnels
          </h3>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costsData} layout="horizontal">
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Coût par opération</span>
              <span className="font-medium text-blue-600">{formatCurrency(cost_per_operation)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-gray-600">Tendance efficacité</span>
              <span className={`text-sm font-medium ${cost_efficiency_trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {cost_efficiency_trend >= 0 ? '+' : ''}{cost_efficiency_trend.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">📍</span>
            Répartition par Région
          </h3>
          
          <div className="space-y-3">
            {regionsData.slice(0, 6).map((region, index) => {
              const maxCount = Math.max(...regionsData.map(r => r.count));
              const percentage = (region.count / maxCount) * 100;
              
              return (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{region.name}</span>
                    <span className="text-gray-600">{region.count} op.</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Opportunités d'amélioration */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">💡</span>
          Opportunités d'Amélioration
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {improvement_opportunities.map((opportunity, index) => (
            <div key={index} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900">{opportunity.area}</div>
                <span className="text-2xl">💰</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Économies potentielles: <span className="font-medium text-green-600">
                  {formatCurrency(opportunity.potential_savings)}
                </span>
              </div>
              <button 
                onClick={() => onNavigate('improvement-detail', { area: opportunity.area })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Voir le plan d'action →
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">⚡</span>
          Actions Rapides
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('incident-report')}
            className="bg-red-500 text-white p-4 rounded-lg hover:bg-red-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">🚨</span>
            <span className="text-sm">Signaler Incident</span>
          </button>
          
          <button
            onClick={() => onNavigate('maintenance-request')}
            className="bg-orange-500 text-white p-4 rounded-lg hover:bg-orange-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">🔧</span>
            <span className="text-sm">Demande Maintenance</span>
          </button>
          
          <button
            onClick={() => onNavigate('route-optimization')}
            className="bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">🗺️</span>
            <span className="text-sm">Optimiser Routes</span>
          </button>
          
          <button
            onClick={() => onNavigate('performance-report')}
            className="bg-green-500 text-white p-4 rounded-lg hover:bg-green-600 transition-colors flex flex-col items-center"
          >
            <span className="text-2xl mb-2">📊</span>
            <span className="text-sm">Rapport Performance</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperationsSection;